import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, Send, Sparkles, RotateCcw, User2, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { trackMentorMessage } from "../lib/analytics";

import { API, safeJson } from "../lib/api";
// API now resolved at runtime via /lib/api.js
/**
 * AIMentor — Chat UI for the Academy AI mentor (Claude Sonnet 4.5 backend)
 * Props:
 *   lang: 'es' | 'en' | 'sk'
 *   labels: localized UI strings
 *   lessonTitle?: string
 *   lessonLevel?: string
 */
export const AIMentor = ({ lang = "es", labels = {}, lessonTitle, lessonLevel, onAfterMessage, trialRemaining = null }) => {
  const { token, isPro } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: labels.mentor_welcome || "Welcome." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [useLessonCtx, setUseLessonCtx] = useState(true);
  const scrollRef = useRef(null);

  // Reset welcome message when language changes
  useEffect(() => {
    setMessages([{ role: "assistant", content: labels.mentor_welcome || "Welcome." }]);
    setSessionId(null);
  }, [lang, labels.mentor_welcome]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;

    const newUserMsg = { role: "user", content: text };
    const history = messages.slice(1); // drop welcome msg
    setMessages((prev) => [...prev, newUserMsg]);
    setInput("");
    setSending(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: text,
          lang,
          lesson_title: useLessonCtx ? lessonTitle : null,
          lesson_level: useLessonCtx ? lessonLevel : null,
          history,
        }),
      });
      if (res.status === 402 || res.status === 401) {
        // Trial exhausted or auth required — refresh parent so PremiumGate takes over.
        if (onAfterMessage) { try { onAfterMessage(); } catch {} }
        // Roll back the optimistic user message so the chat doesn't look stuck.
        setMessages((prev) => prev.slice(0, -1));
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await safeJson(res);
      setSessionId(data.session_id);
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      try {
        trackMentorMessage({ source: "academy", isPro, messageLength: text.length });
      } catch {}
      if (onAfterMessage) {
        try { onAfterMessage(); } catch {}
      }
    } catch (e) {
      if (onAfterMessage) { try { onAfterMessage(); } catch {} }
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: labels.mentor_error || "Error" },
      ]);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setMessages([{ role: "assistant", content: labels.mentor_welcome || "Welcome." }]);
    setSessionId(null);
    setInput("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-3 py-1.5 backdrop-blur">
          <Bot className="h-3.5 w-3.5 text-cyan-300" />
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-cyan-200">
            {labels.mentor_title}
          </span>
        </div>
        {trialRemaining !== null && trialRemaining > 0 && (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-200"
            data-testid="mentor-trial-badge"
          >
            🎁 {labels.trial_one_chat || "1 free message"}
          </span>
        )}
      </div>
      <h2 className="font-display text-3xl font-black tracking-tighter text-white md:text-5xl">
        {labels.mentor_title}
      </h2>
      <p className="mt-2 text-base text-white/55">{labels.mentor_sub}</p>

      {/* Context toggle */}
      {lessonTitle && (
        <label
          className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[11px] text-white/60 transition-colors hover:border-white/25 hover:text-white"
          data-testid="mentor-context-toggle"
        >
          <input
            type="checkbox"
            checked={useLessonCtx}
            onChange={(e) => setUseLessonCtx(e.target.checked)}
            className="h-3.5 w-3.5 accent-orange-400"
          />
          <Sparkles className="h-3 w-3 text-orange-300" />
          <span>
            {labels.mentor_use_lesson} · <span className="text-orange-300">{lessonTitle}</span>
          </span>
        </label>
      )}

      {/* Chat panel */}
      <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-950/90 to-black/70 backdrop-blur-xl">
        {/* messages */}
        <div
          ref={scrollRef}
          className="h-[480px] overflow-y-auto px-4 py-5 md:px-6"
          data-testid="mentor-messages"
        >
          <AnimatePresence initial={false}>
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className={`mb-4 flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {m.role === "assistant" && (
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-cyan-400/30 bg-cyan-400/10">
                    <Bot className="h-4 w-4 text-cyan-300" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-gradient-to-br from-orange-500/90 to-amber-600/90 text-black"
                      : "border border-white/10 bg-white/[0.04] text-white/90"
                  }`}
                  data-testid={`mentor-msg-${m.role}-${i}`}
                >
                  {m.content}
                </div>
                {m.role === "user" && (
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-orange-400/40 bg-orange-400/10">
                    <User2 className="h-4 w-4 text-orange-300" />
                  </div>
                )}
              </motion.div>
            ))}
            {sending && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 flex items-center gap-3"
                data-testid="mentor-thinking"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-cyan-400/30 bg-cyan-400/10">
                  <Loader2 className="h-4 w-4 animate-spin text-cyan-300" />
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white/60">
                  {labels.mentor_thinking}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Composer */}
        <div className="border-t border-white/5 bg-black/40 px-3 py-3 md:px-4">
          <div className="flex items-end gap-2">
            <button
              onClick={reset}
              data-testid="mentor-reset-btn"
              title={labels.mentor_clear}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-white/60 transition-all hover:border-white/25 hover:text-white"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={labels.mentor_placeholder}
              rows={1}
              data-testid="mentor-input"
              className="min-h-[44px] max-h-32 flex-1 resize-none rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-cyan-400/50 focus:bg-white/[0.06]"
            />
            <button
              onClick={send}
              disabled={!input.trim() || sending}
              data-testid="mentor-send-btn"
              className="inline-flex h-11 shrink-0 items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-600 px-5 text-xs font-black uppercase tracking-[0.2em] text-black shadow-[0_0_24px_rgba(255,140,0,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,140,0,0.55)] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              <span className="hidden md:inline">{labels.mentor_send}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIMentor;
